// ============================================================================
// Refund worklist API — the /cash-log Refund tab.
//
// A refund raised on the OMS screen fans out into one MONEY LEG per payment
// instrument it was drained from. Some legs settle themselves (a credit
// reversal lowers a debt and moves no money); the rest are payouts a human has
// to make and then record here with a UTR or a refund id.
//
// Amounts cross the wire in paise, with a matching `*_rupees` string for
// display. Render the rupee string — never divide by 100 in the component, or
// two surfaces will round differently.
// ============================================================================
import { message } from "antd";
import axios from "axios";
import { errorActions } from "../../helpers/auth_helper";

export type RefundStatus =
  | "pending"
  | "partial"
  | "partially_settled"
  | "settled"
  | "failed"
  | "released";

export type ReleaseReason = "payout_failed" | "cancelled";

// Set once the unpaid amount went back to the customer's wallet instead of being paid out.
export interface RefundRelease {
  reason: ReleaseReason;
  note: string;
  released_rupees: string;
  released_at: string | null;
}

export type SettlementReference =
  | "utr"
  | "zoho_refund"
  | "gateway_refund"
  | "credit_reversal";

export interface RefundSettlement {
  id?: number;
  amount_rupees: string;
  reference: string;
  reference_type: SettlementReference;
  zoho_refund_id?: string;
  paid_on?: string | null;
  paid_from?: string | null;
  refund_mode?: string | null;
  settled_at: string;
}

export interface RefundPaymentLeg {
  id: number;
  status: RefundStatus;
  amount_paise: string;
  amount_rupees: string;
  outstanding_rupees: string;
  payment_method: string;
  // Whether this leg needs a human: false for a credit reversal (nothing moves), for an already
  // settled leg, and for one that has been marked failed.
  actionable: boolean;
  moves_money: boolean;
  failure_reason: string;
  source_payment_id: string | null;
  settlements: RefundSettlement[];
}

export interface RefundDestinationBank {
  bank_name: string;
  bank_label?: string;
  account_holder_name: string;
  account_number: string;
  ifsc: string;
  is_active?: boolean;
}

export interface RefundDestination {
  kind: "wallet" | "customer" | "unknown";
  // "refund" = the account chosen for this refund; "on_file" = the customer's only active account,
  // shown because none was chosen.
  bank_source?: "refund" | "on_file" | null;
  bank_details?: RefundDestinationBank | null;
  accounts_on_file?: number;
}

export interface RefundRow {
  id: number;
  sales_order_id: number;
  sales_order_number: string | number;
  lead_id?: string | null;
  origin?: string | null;
  zoho_creditnote_id?: string | null;
  customer_name?: string | null;
  client_id?: string | null;
  // Where the money is going. For customer bank refunds the exact account number + holder name
  // live on `destination.bank_details` — finance pays out against this directly without
  // opening the customer page.
  destination?: RefundDestination;
  // The Zoho credit note this refund draws on (e.g. "OMS-CN-878"). Present on wallet-backed
  // refunds; a legacy `RefundOms` row won't carry one, so tolerate null.
  cn_number: string | null;
  status: RefundStatus;
  total_paise: string;
  total_rupees: string;
  // What must actually leave the business — the total minus any credit reversal. Shown beside the
  // total, or a part-credit refund reads as a transfer that never happened.
  payout_paise: string;
  payout_rupees: string;
  settled_rupees: string;
  reason_code: string;
  notes: string;
  created_at: string;
  created_by: string | null;
  requested_by?: string | null;
  payments: RefundPaymentLeg[];
  released?: RefundRelease | null;
}

const BASE = `${process.env.REACT_APP_API_URL}/oms/refunds`;

export type RefundTab = "pending" | "done" | "failed" | "cancelled";

export const fetchRefunds = async (tab: RefundTab | "all" = "pending"): Promise<RefundRow[]> => {
  try {
    const { data } = await axios.get(`${BASE}/`, { params: tab === "all" ? {} : { tab } });
    return data?.results ?? [];
  } catch (error: any) {
    // errorActions already surfaces the server's `detail` when there is one, and falls back to
    // this message otherwise — so it is the only toast raised here.
    errorActions(error, "Could not load refunds. Please try again.");
    return [];
  }
};

// Puts the unpaid amount back in the customer's wallet — whether the payout failed or the refund was
// cancelled. The backend live-checks Zoho first and refuses if the refund was already paid there.
export const releaseRefund = async (
  id: number,
  reason: ReleaseReason,
  note: string
): Promise<boolean> => {
  try {
    const { data } = await axios.post(`${BASE}/${id}/cancel/`, { reason, note });
    message.success(`₹${(Number(data?.released_amount || 0) / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} released back to the wallet`);
    return true;
  } catch (error: any) {
    errorActions(error, "Could not release this refund.");
    return false;
  }
};

// Sets the bank account finance pays this refund to — for refunds that arrived without one, or to
// correct a wrong pick. The latest attach wins over the account chosen when the refund was raised.
export const attachRefundBank = async (
  paymentId: number,
  bankDetailsId: number
): Promise<RefundRow | null> => {
  try {
    const { data } = await axios.post(`${BASE}/payments/${paymentId}/bank/`, {
      bank_details_id: bankDetailsId,
    });
    message.success("Bank account attached");
    return data;
  } catch (error: any) {
    errorActions(error, "Could not attach the bank account.");
    return null;
  }
};

const SO_BASE = `${process.env.REACT_APP_API_URL}/oms/sales-orders`;

export const fetchSalesOrderRefunds = async (salesOrderId: number): Promise<RefundRow[]> => {
  try {
    const { data } = await axios.get(`${SO_BASE}/${salesOrderId}/refunds/`);
    return data?.results ?? [];
  } catch (error: any) {
    errorActions(error, "Could not load this order's refunds.");
    return [];
  }
};

export const attachSalesOrderRefundBank = async (
  salesOrderId: number,
  paymentId: number,
  bankDetailsId: number
): Promise<RefundRow | null> => {
  try {
    const { data } = await axios.post(`${SO_BASE}/${salesOrderId}/refunds/${paymentId}/bank/`, {
      bank_details_id: bankDetailsId,
    });
    message.success("Bank account attached");
    return data;
  } catch (error: any) {
    errorActions(error, "Could not attach the bank account.");
    return null;
  }
};

export interface PayingAccount {
  account_id: string;
  account_name: string;
  account_type: string;
}

export const fetchPayingAccounts = async (): Promise<PayingAccount[]> => {
  try {
    const { data } = await axios.get(`${BASE}/paying-accounts/`);
    return data?.results ?? [];
  } catch (error: any) {
    errorActions(error, "Could not load the Zoho accounts.");
    return [];
  }
};

export const markRefundPaid = async (
  paymentId: number,
  body: { utr: string; amount_paise: string; paid_on: string; from_account_id: string }
): Promise<RefundRow | null> => {
  try {
    const { data } = await axios.post(`${BASE}/payments/${paymentId}/paid/`, body);
    message.success("Refund marked as paid");
    return data;
  } catch (error: any) {
    errorActions(error, "Could not mark this refund as paid.");
    return null;
  }
};
